import { useQuery } from '@tanstack/react-query';

import { PageHeader } from '@/components/AppShell';
import { ErrorState, Loading } from '@/components/DataState';
import { fetchUsageDashboard } from '@/lib/admin-api';
import type { AdminAiOperationMetrics, AdminUsagePeriod } from '@/lib/wire';

const NUMBER = new Intl.NumberFormat('en-NZ');
const OPERATION_LABELS: Record<string, string> = {
  transcription: 'Transcription',
  key_point_extraction: 'Key points',
  moderation: 'Moderation',
  letter_drafting: 'Letter drafting',
};
const OPERATIONS = Object.keys(OPERATION_LABELS);

export function Dashboard() {
  const dashboard = useQuery({
    queryKey: ['usage-dashboard'],
    queryFn: fetchUsageDashboard,
  });

  return (
    <>
      <PageHeader title="Dashboard">
        Candidate activity, referee response rates and estimated AI usage. Calendar months and
        provider billing are measured in UTC.
      </PageHeader>

      {dashboard.isPending && <Loading label="Loading usage dashboard…" />}
      {dashboard.isError && <ErrorState onRetry={() => dashboard.refetch()} />}
      {dashboard.isSuccess && (
        <div className="space-y-5">
          <MeteringNotice
            meteredFrom={dashboard.data.meteredFrom}
            periods={dashboard.data.periods}
          />

          <div className="grid gap-3 lg:grid-cols-3">
            {dashboard.data.periods.map((period) => (
              <PeriodCard key={period.key} period={period} />
            ))}
          </div>

          <AiBreakdown periods={dashboard.data.periods} />

          <p className="font-body text-[11px] leading-relaxed text-ink-70">
            Referee outcomes follow the cohort whose invitation was delivered in each period.
            “No recorded response” means no accept or decline was recorded by the time this
            dashboard was generated. A candidate using both mobile platforms appears in both
            platform figures, but only once in “mobile users”. Historical platform evidence is
            backfilled from push-registration timestamps; candidates without one appear after
            opening an updated app. Generated{' '}
            {formatDateTime(dashboard.data.generatedAt)}.
          </p>
        </div>
      )}
    </>
  );
}

function MeteringNotice({
  meteredFrom,
  periods,
}: {
  meteredFrom: string | null;
  periods: AdminUsagePeriod[];
}) {
  const allTime = periods.find((period) => period.key === 'all_time');
  const unpriced = allTime?.ai.unpricedCalls ?? 0;

  return (
    <aside
      className="rounded-md border border-gold/40 bg-gold-tint/40 px-4 py-3"
      aria-label="AI cost coverage"
    >
      <p className="font-display text-[12.5px] font-semibold text-ink">AI cost coverage</p>
      <p className="mt-1 font-body text-[11.5px] leading-relaxed text-ink-70">
        {meteredFrom === null
          ? 'No provider calls have been metered yet. Dollar totals will begin after this release is deployed.'
          : `Metering begins ${formatDateTime(meteredFrom)}. Provider calls before that point are not backfilled.`}
        {unpriced > 0 &&
          ` ${NUMBER.format(unpriced)} ${unpriced === 1 ? 'call has' : 'calls have'} usage recorded but no verified rate, so ${unpriced === 1 ? 'it is' : 'they are'} excluded from dollar totals.`}
      </p>
    </aside>
  );
}

function PeriodCard({ period }: { period: AdminUsagePeriod }) {
  const accountLabel = period.key === 'all_time' ? 'Accounts' : 'New accounts';
  const mobileLabel = period.key === 'all_time' ? 'Mobile users' : 'Active mobile';
  const range =
    period.startsAt === null
      ? `Through ${formatDate(period.endsAt)}`
      : `${formatDate(period.startsAt)} – ${formatDate(period.endsAt)}`;

  return (
    <section
      className="rounded-lg border border-line bg-surface p-4 shadow-sm"
      aria-labelledby={`period-${period.key}`}
    >
      <div className="border-b border-line pb-3">
        <h2
          id={`period-${period.key}`}
          className="font-display text-[16px] font-semibold text-ink"
        >
          {period.label}
        </h2>
        <p className="mt-0.5 font-body text-[10.5px] text-ink-70">{range}</p>
      </div>

      <h3 className="mt-3 font-display text-[11px] font-semibold uppercase tracking-wide text-ink-70">
        Candidates
      </h3>
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2">
        <Metric label={accountLabel} value={period.users.newAccounts} />
        <Metric label={mobileLabel} value={period.users.mobileUsers} />
        <Metric label="iOS" value={period.users.iosUsers} />
        <Metric label="Android" value={period.users.androidUsers} />
      </dl>

      <h3 className="mt-4 border-t border-line pt-3 font-display text-[11px] font-semibold uppercase tracking-wide text-ink-70">
        Referees
      </h3>
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2">
        <Metric label="Contacted" value={period.referees.contacted} />
        <Metric
          label="Accepted"
          value={period.referees.accepted}
          suffix={`${period.referees.acceptedPercent}%`}
        />
        <Metric
          label="Declined"
          value={period.referees.declined}
          suffix={`${period.referees.declinedPercent}%`}
        />
        <Metric
          label="No response"
          value={period.referees.noResponse}
          suffix={`${period.referees.noResponsePercent}%`}
        />
      </dl>

      <h3 className="mt-4 border-t border-line pt-3 font-display text-[11px] font-semibold uppercase tracking-wide text-ink-70">
        AI
      </h3>
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2">
        <Metric label="Estimated cost" value={formatUsd(period.ai.estimatedCostUsdMicros)} />
        <Metric label="Calls" value={period.ai.calls} />
        <Metric label="Input tokens" value={period.ai.inputTokens} />
        <Metric label="Output tokens" value={period.ai.outputTokens} />
        <Metric label="Audio" value={formatDuration(period.ai.audioSeconds)} />
        <Metric label="Unpriced calls" value={period.ai.unpricedCalls} />
      </dl>
    </section>
  );
}

function Metric({ label, value, suffix }: { label: string; value: number | string; suffix?: string }) {
  return (
    <div>
      <dt className="font-body text-[10.5px] text-ink-70">{label}</dt>
      <dd className="mt-0.5 font-display text-[16px] font-semibold tabular-nums text-ink">
        {typeof value === 'number' ? NUMBER.format(value) : value}
        {suffix && (
          <span className="ml-1 font-body text-[10.5px] font-medium text-ink-70">{suffix}</span>
        )}
      </dd>
    </div>
  );
}

function AiBreakdown({ periods }: { periods: AdminUsagePeriod[] }) {
  return (
    <section
      className="overflow-hidden rounded-lg border border-line bg-surface shadow-sm"
      aria-labelledby="ai-breakdown-title"
    >
      <div className="border-b border-line px-4 py-3">
        <h2 id="ai-breakdown-title" className="font-display text-[15px] font-semibold text-ink">
          AI cost by task
        </h2>
        <p className="mt-0.5 font-body text-[11.5px] text-ink-70">
          Estimated USD from provider-reported usage and the rate saved with each call.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[660px] border-collapse text-left">
          <thead>
            <tr className="border-b border-line bg-paper/60">
              <th
                scope="col"
                className="px-4 py-2.5 font-display text-[10.5px] font-semibold uppercase tracking-wide text-ink-70"
              >
                Task
              </th>
              {periods.map((period) => (
                <th
                  key={period.key}
                  scope="col"
                  className="px-4 py-2.5 font-display text-[10.5px] font-semibold uppercase tracking-wide text-ink-70"
                >
                  {period.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {OPERATIONS.map((operation) => (
              <tr key={operation} className="border-b border-line last:border-b-0">
                <th
                  scope="row"
                  className="px-4 py-3 font-display text-[12.5px] font-medium text-ink"
                >
                  {OPERATION_LABELS[operation]}
                </th>
                {periods.map((period) => {
                  const metrics = findOperation(period, operation);
                  return (
                    <td key={period.key} className="px-4 py-3 align-top">
                      <div className="font-display text-[13px] font-semibold tabular-nums text-ink">
                        {formatUsd(metrics.estimatedCostUsdMicros)}
                      </div>
                      <div className="mt-0.5 font-body text-[10.5px] tabular-nums text-ink-70">
                        {NUMBER.format(metrics.calls)} calls
                        {metrics.unpricedCalls > 0 &&
                          ` · ${NUMBER.format(metrics.unpricedCalls)} unpriced`}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function findOperation(period: AdminUsagePeriod, operation: string): AdminAiOperationMetrics {
  return (
    period.ai.operations.find((item) => item.operation === operation) ?? {
      operation,
      calls: 0,
      inputTokens: 0,
      outputTokens: 0,
      audioSeconds: 0,
      estimatedCostUsdMicros: 0,
      unpricedCalls: 0,
    }
  );
}

function formatUsd(micros: number): string {
  if (micros > 0 && micros < 10_000) return '<$0.01';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    micros / 1_000_000,
  );
}

function formatDuration(seconds: number): string {
  if (seconds === 0) return '0 min';
  if (seconds < 60) return `${Math.round(seconds)} sec`;
  return `${(seconds / 60).toFixed(1)} min`;
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('en-NZ', { dateStyle: 'medium', timeZone: 'UTC' }).format(
    new Date(value),
  );
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('en-NZ', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(new Date(value));
}
